import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Alert, Typography } from "@mui/material";
import { getSurveyLinks, updateAdvisor } from "../services/gutcheck-admin-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import { IAdvisor } from "../../../types";

dayjs.extend(utc);

const statuses = [
    { value: "init", label: "Not Completed" },
    { value: "complete", label: "Completed" },
    { value: "approved", label: "Approved" },
    { value: "screened", label: "Screened Out" },
    { value: "over_quota", label: "Over Quota" },
    { value: "rejected", label: "Rejected" }
];

export const SettingsTab = ({
    advisor,
    onUpdate,
    onFieldUpdate
}: {
    advisor: IAdvisor,
    onUpdate: () => void,
    onFieldUpdate?: (updates: Partial<IAdvisor>) => void
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [surveyLinks, setSurveyLinks] = useState<any>([]);

    const initialAdvisor = {
        name: advisor.user?.profile?.name ?? "",
        email_address: advisor.user?.email_address ?? "",
        invited_to_invest: advisor.invited_to_invest ?? false,
        completed_at: advisor.completed_at ? dayjs.utc(advisor.completed_at).local().format('YYYY-MM-DDTHH:mm') : null,
        survey_link_id: advisor.survey_link?.id ?? "",
        reserved_spot: advisor.reserved_spot ?? false,
        reserved_amount: advisor.reserved_amount ?? 0,
        status: advisor.status ?? "init",
        funded: advisor.funded ?? false,
        funded_amount: advisor.funded_amount ?? 0,
        referred_by_code: advisor.referred_by_code ?? "",
        latest_survey_url: advisor.latest_survey_url ?? "",
        progress: advisor.progress ?? "",
        referrer_code: advisor.referrer_code ?? "",
        is_test: advisor.is_test ?? false,
        personalized_followups: advisor.personalized_followups ?? false,
        insights_enabled: advisor.insights_enabled ?? false
    };

    const [advisorData, setAdvisorData] = useState<any>(initialAdvisor);

    // Auto-clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Update form data when advisor prop changes
    useEffect(() => {
        const newInitialAdvisor = {
            name: advisor.user?.profile?.name ?? "",
            email_address: advisor.user?.email_address ?? "",
            invited_to_invest: advisor.invited_to_invest ?? false,
            completed_at: advisor.completed_at ? dayjs.utc(advisor.completed_at).local().format('YYYY-MM-DDTHH:mm') : null,
            survey_link_id: advisor.survey_link?.id ?? "",
            reserved_spot: advisor.reserved_spot ?? false,
            reserved_amount: advisor.reserved_amount ?? 0,
            status: advisor.status ?? "init",
            funded: advisor.funded ?? false,
            funded_amount: advisor.funded_amount ?? 0,
            referred_by_code: advisor.referred_by_code ?? "",
            latest_survey_url: advisor.latest_survey_url ?? "",
            progress: advisor.progress ?? "",
            referrer_code: advisor.referrer_code ?? "",
            is_test: advisor.is_test ?? false,
            personalized_followups: advisor.personalized_followups ?? false,
            insights_enabled: advisor.insights_enabled ?? false
        };
        setAdvisorData(newInitialAdvisor);
    }, [advisor]);

    useEffect(() => {
        const fetchSurveyLinks = async () => {
            if (!advisor.survey_link?.project?.id) return;

            try {
                const accessToken = await getAccessTokenSilently();
                const { data, error } = await getSurveyLinks(advisor.survey_link.project.id, accessToken);
                if (data) {
                    setSurveyLinks(data.surveyLinks);
                } else {
                    console.error(error);
                }
            } catch (error) {
                console.error('Failed to fetch survey links:', error);
            }
        };

        fetchSurveyLinks();
    }, [advisor.survey_link?.project?.id]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        if (!advisor.survey_link?.project?.id) {
            setErrorMessage("Project ID is required");
            setIsSubmitting(false);
            return;
        }

        try {
            const accessToken = await getAccessTokenSilently();
            const newAdvisor = { ...advisorData };
            newAdvisor.completed_at = advisorData.completed_at ? new Date(advisorData.completed_at).toISOString() : null;

            await updateAdvisor(advisor.survey_link.project.id, advisor.id, newAdvisor, accessToken);
            setSuccessMessage("Advisor settings updated successfully");

            // Update the local advisor state to reflect the saved changes
            if (onFieldUpdate) {
                onFieldUpdate(newAdvisor);
            }
        } catch (error) {
            setErrorMessage("An error occurred while updating advisor settings");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setAdvisorData({ ...advisorData, [event.target.name]: value });
    };

    if (!surveyLinks.length) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Alert severity="info">Loading survey links...</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>

            <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2} sx={{ marginTop: '20px' }}>
                    <TextField
                        label="Survey Progress"
                        name="progress"
                        value={advisorData.progress}
                        disabled
                        size="small"
                    />

                    <TextField
                        label="Referred By Code"
                        name="referred_by_code"
                        value={advisorData.referred_by_code}
                        disabled
                        size="small"
                    />

                    <TextField
                        label="Survey Link"
                        name="survey_link_id"
                        value={advisorData.survey_link_id}
                        onChange={handleChange}
                        select
                        required
                        size="small"
                    >
                        {surveyLinks.map((surveyLink: any) => (
                            <MenuItem key={surveyLink.id} value={surveyLink.id}>{surveyLink.name}</MenuItem>
                        ))}
                    </TextField>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Viewpoint Status"
                            name="status"
                            value={advisorData.status}
                            onChange={handleChange}
                            select
                            required
                            size="small"
                            sx={{ flexGrow: 1 }}
                        >
                            {statuses.map((status: any) => (
                                <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.is_test}
                                    onChange={handleChange}
                                    name="is_test"
                                />
                            }
                            label="Test User"
                        />
                    </Stack>

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.invited_to_invest}
                                    onChange={handleChange}
                                    name="invited_to_invest"
                                />
                            }
                            label="Invited to Invest"
                        />
                        <TextField
                            label="Completed At"
                            name="completed_at"
                            value={advisorData.completed_at || ''}
                            onChange={handleChange}
                            type="datetime-local"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{ width: '65%' }}
                            size="small"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.reserved_spot}
                                    onChange={handleChange}
                                    name="reserved_spot"
                                />
                            }
                            label="Reserved Spot"
                        />
                        <TextField
                            label="Reserved Amount"
                            name="reserved_amount"
                            value={advisorData.reserved_amount}
                            onChange={handleChange}
                            type="number"
                            size="small"
                            sx={{ width: '65%' }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.funded}
                                    onChange={handleChange}
                                    name="funded"
                                />
                            }
                            label="Funded"
                        />
                        <TextField
                            label="Funded Amount"
                            name="funded_amount"
                            value={advisorData.funded_amount}
                            onChange={handleChange}
                            type="number"
                            size="small"
                            sx={{ width: '65%' }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.personalized_followups}
                                    onChange={handleChange}
                                    name="personalized_followups"
                                />
                            }
                            label="Personalized onboarding followups"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advisorData.insights_enabled}
                                    onChange={handleChange}
                                    name="insights_enabled"
                                />
                            }
                            label="Receives monthly questions"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2, alignItems: 'center' }}>
                        {successMessage && <Typography variant="body1" color="success.main">{successMessage}</Typography>}
                        {errorMessage && <Typography variant="body1" color="error.main">{errorMessage}</Typography>}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            Save Settings
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}; 